var calcHeight,
    calcWidth,
    kind = require("enyo/kind");

require("enyo-ui");

/**
 * Fitable controls must implement a method to calculate the width of the
 * control given an input height. Ideally this should not require any
 * interaction with the DOM.
 *
 * @param    {number}  height  The height
 * @memberof enyo-ui.FitableSupport
 * @method
 * @private
 */
calcWidth = function() {
    this.error("Controls with FitableSupport must implement a `calcWidth` method");
};

/**
 * Fitable controls must implement a method to calculate the height of the
 * control given an input width. Ideally this should not require any interaction
 * with the DOM.
 *
 * @param    {number}  width  The width
 * @memberof enyo-ui.FitableSupport
 * @method
 * @private
 */
calcHeight = function() {
    this.error("Controls with FitableSupport must implement a `calcHeight` method");
};

/**
 * [FitableSupport]{@link enyo-ui.FitableSupport} implements a standard way to
 * scale controls to fit within a certain width and/or height. The general idea
 * is that a fitable component should be able to calculate it's height when
 * given an input width and the other way around.
 *
 * @mixin enyo-ui.FitableSupport
 * @protected
 */
module.exports = {

    /**
     * @private
     */
    name: "enyo-ui.FitableSupport",

    /**
     * @private
     */
    classes: "enyo-ui-fitable",

    /**
     * Defines a cachedBounds property on the kind instance.
     *
     * @memberof enyo-ui.FitableSupport
     * @method
     * @private
     */
    constructed: kind.inherit(function(sup) {
        return function() {
            this.calcHeight   = this.calcHeight ? this.calcHeight : calcHeight;
            this.calcWidth    = this.calcWidth ? this.calcWidth : calcWidth;
            this.cachedBounds = null;
            sup.apply(this, arguments);
        };
    }),

    /**
     * Extends setBounds such that the control can be sized by providing only a
     * width or height.
     *
     * This method will reflow the control and all it's children if the bounds
     * changed.
     *
     * @param    {Object}   bounds  The bounds
     * @param    {string=}  unit    The css unit
     * @memberof enyo-ui.FitableSupport
     * @method
     * @private
     */
    setBounds: kind.inherit(function(sup) {
        return function(bounds, unit) {
            var cachedBounds;

            // Calculate height if needed
            if (bounds.width && bounds.height === undefined) {
                bounds.height = this.calcHeight(bounds.width);
            }

            // Calculate width if needed
            else if (bounds.height && bounds.width === undefined) {
                bounds.width = this.calcWidth(bounds.height);
            }

            // Set bounds
            sup.call(this, bounds, unit);

            // Trigger a reflow if bounds changed, unless we're already
            // reflowing.
            cachedBounds = this.cachedBounds;
            bounds       = this.getBounds();

            if (!this.isReflowing
                && bounds
                && (!cachedBounds
                    || cachedBounds.width !== bounds.width
                    || cachedBounds.height !== bounds.height
                )) {
                this.resize();
            }
        };
    }),

    /**
     * Ensure the bounds are cached after reflow, allowing performance tweaks to
     * be implemented by parent components.
     *
     * @memberof enyo-ui.FitableSupport
     * @method
     * @private
     */
    reflow: kind.inherit(function(sup) {
        return function() {
            this.set("isReflowing", true);
            this.set("cachedBounds", null);
            sup.apply(this, arguments);
            this.set("cachedBounds", this.getBounds() || null);
            this.set("isReflowing", false);
        };
    }),

    /**
     * Scale the control to the smallest size capable of covering the provided
     * dimensions.
     *
     * @param    {number}  width   The width
     * @param    {number}  height  The height
     * @memberof enyo-ui.FitableSupport
     * @public
     */
    cover: function(width, height) {
        var widthByHeight,
            heightByWidth;

        widthByHeight = this.calcWidth(height);
        heightByWidth = this.calcHeight(width);

        if (widthByHeight >= width) {
            this.setBounds({
                width:  widthByHeight,
                height: height
            });
        }
        else {
            this.setBounds({
                width:  width,
                height: heightByWidth
            });
        }
    },

    /**
     * Scale the control to the largest size capable of fitting within the
     * provided dimensions.
     *
     * @param    {number}  width   The width
     * @param    {number}  height  The height
     * @memberof enyo-ui.FitableSupport
     * @public
     */
    contain: function(width, height) {
        var widthByHeight,
            heightByWidth;

        widthByHeight = this.calcWidth(height);
        heightByWidth = this.calcHeight(width);

        if (widthByHeight <= width) {
            this.setBounds({
                width:  widthByHeight,
                height: height
            });
        }
        else {
            this.setBounds({
                width:  width,
                height: heightByWidth
            });
        }
    }

};
